import React, { useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";
import { Editor, EditorState, ContentState, convertFromRaw } from "draft-js";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { fileExtensionIsImage } from "../../../_utils/generalUtils";
import { ADR_REVIEW } from "../../../_constants/actionConstants";
import { adrSections } from "../../../_constants/docSections";
import { AdrCorrectionCommentsV3 } from "./AdrCorrectionCommentsV3";
import { useCallback } from "react";
import adrActions from "../../../_actions/adrActions";

export function AdrReviewGeneralCommentsV3(props) {
  const { tabId, userIsReviewer } = props;
  const adr = useSelector((state) => state.adr.openAdrs[tabId] || null);
  const dispatch = useDispatch();

  const user = useSelector((state) => state.authentication.user || null);

  const gcIsBlank = useCallback(
    (gc) => !gc.subject && !gc.narrative && gc.keywordIds.length === 0,
    []
  );
  const gcs = useMemo(
    () => (adr.generalComments || []).filter((gc) => !gcIsBlank(gc)),
    [adr.generalComments]
  );

  const addCorrectionComment = (fieldInfo) => {
    // Other conditions (such as userIsReviewer and isInReview) are checked by the calling child
    // components
    if (user)
      dispatch({
        type: ADR_REVIEW.CORRECTION_COMMENTS.ADD_BLANK,
        tabId,
        byUserId: user.userId,
        section: adrSections.ADR_SECTION_GENERALCOMMENTS,
        fieldInfo,
      });
  };

  const hasCorrectionComment = useCallback(
    (fieldInfoToLookup) => {
      const baseCcs = (adr.correctionComments || []).filter(
        (cc) =>
          cc.section === adrSections.ADR_SECTION_GENERALCOMMENTS &&
          cc.fieldInfo &&
          "gcClientId" in cc.fieldInfo &&
          cc.fieldInfo.gcClientId === fieldInfoToLookup.gcClientId
      );
      const cc =
        fieldInfoToLookup.name === "attachment"
          ? baseCcs.find((cc) => cc.fieldInfo.caClientId === fieldInfoToLookup.caClientId)
          : baseCcs.find((cc) => cc.fieldInfo.name === fieldInfoToLookup.name);
      return !!cc;
    },
    [adr.correctionComments]
  );

  const filteredCcs = useMemo(
    () =>
      adr
        ? (adr.correctionComments || []).filter(
            (cc) => cc.section === adrSections.ADR_SECTION_GENERALCOMMENTS
          )
        : [],
    [adr.correctionComments]
  );

  return (
    <React.Fragment>
      <h4>General Comments</h4>
      <div className="row">
        <div className="col-lg-9">
          {gcs.length > 0 ? (
            gcs.map((gc) => {
              const metadataBase = {
                gcClientId: gc.clientId,
                reviewerInitials: props.reviewerInitials,
              };
              return (
                <AdrReviewGeneralCommentRowV3
                  key={`gcRowFragKey_${gc.clientId}`}
                  generalComment={gc}
                  metadataBase={metadataBase}
                  isInReview={adr.isInReview}
                  projectId={adr.projectId}
                  addCorrectionComment={addCorrectionComment}
                  hasCorrectionComment={hasCorrectionComment}
                  {...props}
                />
              );
            })
          ) : (
            <div key={`gcRowKey_empty`} className="row">
              <div className="col-12">No comments</div>
            </div>
          )}
        </div>
        <div className="col-lg-3 col-md-12 comment-column border-left">
          <AdrCorrectionCommentsV3
            section={adrSections.ADR_SECTION_GENERALCOMMENTS}
            correctionComments={filteredCcs}
            {...props}
          />
        </div>
      </div>
    </React.Fragment>
  );
}

function AdrReviewGeneralCommentRowV3(props) {
  const {
    generalComment: gc,
    projectId,
    metadataBase,
    isInReview,
    hasCorrectionComment,
    addCorrectionComment,
    userIsReviewer,
  } = props;
  const dispatch = useDispatch();

  const handleClick_field = (fieldInfo) => (e) => {
    e.preventDefault();
    if (!hasCorrectionComment(fieldInfo) && isInReview && userIsReviewer)
      addCorrectionComment(fieldInfo);
  };

  const handleClick_openFile = (filePath) => (e) => {
    e.preventDefault();
    e.stopPropagation();
    dispatch(adrActions.openFile(filePath));
  };

  const renderAttachmentThumbnail = (ca) => {
    const ext = ca.originalFileName ? ca.originalFileName.split(".").pop().toLowerCase() : "";
    if (fileExtensionIsImage(ext)) {
      return (
        <div onClick={handleClick_openFile(ca.filePath)} className="pointer-cursor">
          <img src={ca.thumbnailBase64} alt="(Cannot display thumbnail)" height="90" width="auto" />
        </div>
      );
    }
    let icon = "file";
    if (["doc", "docx"].includes(ext)) icon = "file-word";
    else if (["xls", "xlsx"].includes(ext)) icon = "file-excel";
    else if (ext === "pdf") icon = "file-pdf";
    else if (ext === "zip") icon = "file-archive";
    return (
      <div onClick={handleClick_openFile(ca.filePath)} className="pointer-cursor">
        <FontAwesomeIcon icon={["far", icon]} className="fa-fw fa-3x" />
      </div>
    );
  };

  const projKeywordList = useSelector(
    (state) => state.keywords.projKeywordLists[projectId] || null
  );

  const keywords = projKeywordList
    ? projKeywordList.filter((kw) => gc.keywordIds.includes(kw.keywordId))
    : [];

  const subLabel = `for Comment #${gc.clientId + 1}`;

  const subjectInfo = {
    ...metadataBase,
    name: "subject",
    value: gc.subject,
    label: "Subject",
    subLabel,
  };

  const keywordInfo = {
    ...metadataBase,
    name: "keywordIds",
    value: gc.keywordIds,
    label: "Keywords",
    subLabel,
  };

  const narrativeInfo = {
    ...metadataBase,
    name: "narrative",
    value: gc.narrative,
    label: "Narrative",
    subLabel,
  };

  const editorState = useMemo(
    () =>
      gc.narrativeDraftState
        ? EditorState.createWithContent(
            convertFromRaw(JSON.parse(gc.narrativeDraftState))
          )
        : gc.narrative
        ? EditorState.createWithContent(ContentState.createFromText(gc.narrative))
        : EditorState.createWithContent(ContentState.createFromText("(None)")),
    [gc.narrativeDraftState, gc.narrative]
  );

  const editorIsEmpty =
    !editorState.getCurrentContent().hasText() ||
    ["", "(None)"].includes(editorState.getCurrentContent().getPlainText());

  return (
    <React.Fragment>
      <div key={`gcRowKey_${gc.clientId}`} className="d-flex flex-column">
        <div className="row">
          <div className="col-12">
            <b className="text-muted">
              <em>Comment #{gc.clientId + 1}</em>
            </b>
          </div>
        </div>
        <div className="row my-1">
          <div className="col-2">
            <b>Subject:</b>
          </div>
          <div className="col-10">
            <div
              className={`${userIsReviewer && isInReview ? "reviewable-item" : ""} ${
                hasCorrectionComment(subjectInfo) ? "has-comment" : ""
              }`}
              onClick={handleClick_field(subjectInfo)}
            >
              {gc.subject}
            </div>
          </div>
        </div>
        <div className="row my-1">
          <div className="col-2">
            <b>Keywords:</b>
          </div>
          <div className="col-10">
            <div
              className={`pl-1 ${userIsReviewer && isInReview ? "reviewable-item" : ""} ${
                hasCorrectionComment(keywordInfo) ? "has-comment" : ""
              }`}
              onClick={handleClick_field(keywordInfo)}
            >
              {keywords.length > 0 ? (
                keywords.map((kw, kwIndex) => (
                  <span
                    key={`kwLabelKey_${kwIndex}`}
                    className="span-keyword-review p-1 mr-1"
                  >
                    {kw.keywordTag}
                  </span>
                ))
              ) : (
                <span key="kwLabelKey_none" className="span-keyword-review p-1 mr-1">
                  (None)
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="row my-1">
          <div className="col-2">
            <b>Comments:</b>
          </div>
          <div className="col-10">
            <div
              className={`${userIsReviewer && isInReview ? "reviewable-item" : ""} ${
                hasCorrectionComment(narrativeInfo) ? "has-comment" : ""
              } ${editorIsEmpty ? "editor-empty" : ""}`}
              onClick={handleClick_field(narrativeInfo)}
            >
              <div
                style={{ overflowX: "auto", borderRadius: "3px" }}
                className="review border"
              >
                <Editor
                  name="narrative"
                  id={`gcNarrative_${gc.clientId}`}
                  editorState={editorState}
                  onChange={() => {}}
                  onBlur={() => {}}
                  readOnly={true}
                />
              </div>
            </div>
          </div>
        </div>
        {gc.commentAttachments && gc.commentAttachments.filter((ca) => !ca.isDeleted).length > 0 && (
          <React.Fragment>
            <div className="row my-1">
              <div className="col-2">
                <b>Attachments:</b>
              </div>
            </div>
            {gc.commentAttachments
              .filter((ca) => !ca.isDeleted)
              .map((ca, caIndex) => {
                const caAttachmentInfo = {
                  ...metadataBase,
                  caClientId: ca.clientId,
                  name: "attachment",
                  value: ca.clientId,
                  label: "Attachment",
                  subLabel: `(${ca.originalFileName}) for Comment #${gc.clientId + 1}`,
                };
                return (
                  <div
                    key={`caRowKey_${caIndex}`}
                    className={`row m-1 px-1 ${
                      userIsReviewer && isInReview ? "reviewable-item" : ""
                    } ${hasCorrectionComment(caAttachmentInfo) ? "has-comment" : ""}`}
                    onClick={handleClick_field(caAttachmentInfo)}
                  >
                    <div className="col-md-3">{renderAttachmentThumbnail(ca)}</div>
                    <div className="col-md-8">
                      <small>
                        <b>Filename:</b> {ca.originalFileName}
                        <br />
                        <b>Created On:</b> {moment(ca.createdOn).format("LLLL")}
                        <br />
                        <b>Description:</b> {ca.description}
                      </small>
                    </div>
                  </div>
                );
              })}
          </React.Fragment>
        )}
      </div>
      <hr className="my-1" />
    </React.Fragment>
  );
}
